import { forwardRef, useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label rendered above the field. */
  label?: ReactNode;
  /** Helper text rendered below the field when no error is set. */
  helperText?: ReactNode;
  /** Error message rendered below the field; when set, the field gets an error border. */
  error?: ReactNode;
  /** Override the wrapper class. */
  containerClassName?: string;
}

/**
 * Multi-line text input. Mirrors `<Input>` API but renders a `<textarea>`.
 *
 * The native focus ring is delegated to the wrapper via `focus-within` so it
 * stays consistent with `<Input>`. Sets `min-h` to keep visual rhythm; consumer
 * can extend via `className`.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, helperText, error, containerClassName, className, id, disabled, rows = 4, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = error || helperText ? `${fieldId}-msg` : undefined;
  const hasError = Boolean(error);

  return (
    <div className={cn('flex flex-col gap-1.5', containerClassName)}>
      {label ? (
        <label htmlFor={fieldId} className="text-sm font-medium text-white">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-describedby={messageId}
        className={cn(
          'min-h-[88px] w-full rounded-card border bg-bg-input px-3 py-2 text-sm text-white ' +
            'placeholder:text-muted resize-y transition-colors outline-none ' +
            'focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-bg ' +
            'disabled:cursor-not-allowed disabled:opacity-60',
          hasError ? 'border-danger' : 'border-border',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={messageId} className="text-xs text-danger">
          {error}
        </p>
      ) : helperText ? (
        <p id={messageId} className="text-xs text-muted">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
